import type { Payment } from "@payezy/api-client/public";
import { approvePaymentMutation, cancelPaymentMutation, getPaymentQueryKey, getPaymentOptions, listAccountsQueryKey, listPaymentsOptions, listPaymentsQueryKey, rejectPaymentMutation } from "@payezy/api-client/public/query";
import { Alert, Badge, Button, Card, DescriptionList, Dialog, EmptyState, Field, PageHeader, Select, Spinner, Table, TD, TH, THead, TR, Textarea, formatDate, formatUSD, humanize, toast } from "@payezy/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, Send } from "lucide-react";
import { useState } from "react";
import { handleAuthProblem, problemMessage } from "../lib/api";
import { useOrg } from "../lib/org";
import { paymentTitle, paymentTone, railName } from "../lib/payments";
import { SendMoneyDialog } from "./SendMoney";

const settling = new Set(["created", "pending_approval", "approved", "processing", "submitted"]);

function PaymentDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const org = useOrg();
  const qc = useQueryClient();
  const path = { orgId: org.org_id, paymentId: id };
  const q = useQuery({
    ...getPaymentOptions({ path }),
    refetchInterval: (query) => (query.state.data && settling.has(query.state.data.payment.status) ? 3000 : false),
  });
  const p = q.data?.payment;
  const [comment, setComment] = useState("");
  const done = (msg: string) => () => {
    toast.success(msg);
    setComment("");
    void qc.invalidateQueries({ queryKey: getPaymentQueryKey({ path }) });
    void qc.invalidateQueries({ queryKey: listPaymentsQueryKey({ path: { orgId: org.org_id } }) });
    void qc.invalidateQueries({ queryKey: listAccountsQueryKey({ path: { orgId: org.org_id } }) });
  };
  const onError = (e: unknown) => { if (!handleAuthProblem(e)) toast.error(problemMessage(e)); };
  const approve = useMutation({ ...approvePaymentMutation(), onSuccess: done("Approved"), onError });
  const reject = useMutation({ ...rejectPaymentMutation(), onSuccess: done("Rejected"), onError });
  const cancel = useMutation({ ...cancelPaymentMutation(), onSuccess: done("Payment canceled"), onError });
  const approved = q.data?.approvals.filter((a) => a.decision === "approve").length ?? 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={p ? paymentTitle(p) : "Payment"} description={p ? `${p.direction === "inbound" ? "+" : "−"}${formatUSD(p.amount)}` : undefined}>
      {q.isPending ? (
        <div className="flex justify-center p-6"><Spinner /></div>
      ) : q.isError ? (
        <Alert tone="danger">{problemMessage(q.error)}</Alert>
      ) : p ? (
        <div className="grid gap-5">
          <DescriptionList
            items={[
              { label: "Status", value: <Badge tone={paymentTone(p.status)}>{humanize(p.status)}</Badge> },
              ...(p.recipient ? [{ label: "To", value: `${p.recipient}${p.recipient_account ? ` · ${p.recipient_account}` : ""}` }] : []),
              { label: "Method", value: railName(p.rail) },
              ...(Number(p.fee) > 0 ? [{ label: "Fee", value: formatUSD(p.fee) }] : []),
              ...(p.dest_asset ? [{ label: "Delivered as", value: `${p.dest_asset}${p.dest_network ? ` on ${humanize(p.dest_network)}` : ""}` }] : []),
              ...(p.tx_hash ? [{ label: "Transaction", value: <span className="break-all font-mono text-xs">{p.tx_hash}</span> }] : []),
              { label: "Created", value: formatDate(p.created_at, true) },
              ...(p.completed_at ? [{ label: "Finished", value: formatDate(p.completed_at, true) }] : []),
              ...(p.memo ? [{ label: "Memo", value: p.memo }] : []),
              ...(p.failure ? [{ label: "Reason", value: `${p.failure.reason}${p.failure.code ? ` (${p.failure.code})` : ""}` }] : []),
              { label: "Reference", value: <span className="font-mono text-xs">{p.id}</span> },
            ]}
          />
          {(p.approvals_required ?? 0) > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium text-ink-800">Approvals ({approved} of {p.approvals_required})</h3>
              {q.data.approvals.length ? (
                <ul className="grid gap-2">
                  {q.data.approvals.map((a) => (
                    <li key={a.user_id} className="text-sm">
                      <Badge tone={a.decision === "approve" ? "success" : "danger"}>{a.decision === "approve" ? "Approved" : "Rejected"}</Badge>{" "}
                      <span className="text-ink-700">{a.email ?? a.user_id}</span>{" "}
                      <span className="text-xs text-ink-400">{formatDate(a.at, true)}</span>
                      {a.comment && <p className="text-ink-500">“{a.comment}”</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-ink-500">Waiting for an approver.</p>
              )}
            </div>
          )}
          {q.data.can_approve && (
            <div className="grid gap-3 rounded-lg bg-ink-50 p-4">
              <Field label="Comment (optional)">
                <Textarea rows={2} maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)} />
              </Field>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" loading={reject.isPending} onClick={() => reject.mutate({ path, body: { comment } })}>Reject</Button>
                <Button loading={approve.isPending} onClick={() => approve.mutate({ path, body: { comment } })}>Approve {formatUSD(p.amount)}</Button>
              </div>
            </div>
          )}
          {q.data.legs.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium text-ink-800">Execution</h3>
              <ul className="grid gap-1 text-sm">
                {q.data.legs.map((l) => (
                  <li key={l.seq} className="flex items-center justify-between gap-3">
                    <span className="text-ink-700">{humanize(l.operation)} via {humanize(l.partner)}{l.reference ? <span className="ml-2 font-mono text-xs text-ink-400">{l.reference}</span> : null}</span>
                    <Badge tone={paymentTone(l.status)}>{humanize(l.status)}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <h3 className="mb-2 text-sm font-medium text-ink-800">Timeline</h3>
            <ol className="grid gap-3 border-l border-ink-200 pl-4">
              {q.data.events.map((e, i) => (
                <li key={i} className="text-sm">
                  <p className="font-medium text-ink-800">{humanize(e.status)}</p>
                  <p className="text-ink-500">{e.message}</p>
                  <p className="text-xs text-ink-400">{formatDate(e.at, true)}</p>
                </li>
              ))}
            </ol>
          </div>
          {q.data.can_cancel && (
            <div className="flex justify-end">
              <Button variant="secondary" loading={cancel.isPending} onClick={() => cancel.mutate({ path })}>Cancel payment</Button>
            </div>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}

export function PaymentRow({ p, onOpen }: { p: Payment; onOpen: (id: string) => void }) {
  const inbound = p.direction === "inbound";
  return (
    <TR className="cursor-pointer hover:bg-ink-50" onClick={() => onOpen(p.id)}>
      <TD className="whitespace-nowrap text-ink-500">{formatDate(p.created_at)}</TD>
      <TD>
        <span className="inline-flex items-center gap-2">
          {inbound ? <ArrowDownLeft className="size-4 text-emerald-600" /> : <ArrowUpRight className="size-4 text-ink-500" />}
          {paymentTitle(p)}
        </span>
      </TD>
      <TD className="text-ink-500">{railName(p.rail)}</TD>
      <TD><Badge tone={paymentTone(p.status)}>{humanize(p.status)}</Badge></TD>
      <TD className={`text-right font-mono ${inbound ? "text-emerald-700" : ""}`}>{inbound ? "+" : "−"}{formatUSD(p.amount)}</TD>
    </TR>
  );
}

export function PaymentsPage() {
  const org = useOrg();
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const q = useQuery({
    ...listPaymentsOptions({ path: { orgId: org.org_id }, query: { limit: 100, ...(status ? { status } : {}) } }),
    refetchInterval: (query) => (query.state.data?.items.some((p) => settling.has(p.status)) ? 3000 : false),
  });
  return (
    <>
      <PageHeader
        title="Payments"
        description="Money in and out of your accounts"
        actions={
          <div className="flex gap-2">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44" aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="pending_approval">Needs approval</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="returned">Returned</option>
          </Select>
          {org.can("payments:create") && <Button onClick={() => setSending(true)}><Send className="size-4" /> Send money</Button>}
          </div>
        }
      />
      {q.isError && <Alert tone="danger">{problemMessage(q.error)}</Alert>}
      <Card>
        {q.isPending ? (
          <div className="flex justify-center p-8"><Spinner /></div>
        ) : q.data?.items.length ? (
          <Table>
            <THead><tr><TH>Date</TH><TH>Payment</TH><TH>Method</TH><TH>Status</TH><TH className="text-right">Amount</TH></tr></THead>
            <tbody>
              {q.data.items.map((p) => <PaymentRow key={p.id} p={p} onOpen={setOpen} />)}
            </tbody>
          </Table>
        ) : (
          <EmptyState icon={<ArrowLeftRight className="size-6" />} title="No payments yet" description="Deposits and transfers will appear here." />
        )}
      </Card>
      {open && <PaymentDialog id={open} onClose={() => setOpen(null)} />}
      {sending && <SendMoneyDialog onClose={() => setSending(false)} />}
    </>
  );
}
